/**
 * Processing page � Step 4 of workflow.
 * Timer lives here at page level: starts on mount, stops only when job fully completes
 * (dialing + AI analysis + export all done).
 */

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { verifyApi } from '../services/api';
import { ProgressTracker } from '../components/ProgressTracker';
import { ResultsView } from '../components/ResultsView';
import { AnalysisTab } from '../components/AnalysisTab';
import { useVerificationStatus } from '../hooks/useVerificationStatus';

const parseServerTimestampMs = (value?: string | null): number | null => {
  if (!value) return null;
  const hasZone = /([zZ]|[+-]\d{2}:\d{2})$/.test(value);
  const normalized = hasZone ? value : `${value}Z`;
  const ms = Date.parse(normalized);
  return Number.isFinite(ms) ? ms : null;
};

const Processing: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const { resetState } = useApp();

  const [done, setDone] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'dialer' | 'analysis'>('dialer');
  const [analysisActive, setAnalysisActive] = useState(false);

  // -- Page-level elapsed timer -----------------------------------------------
  // Persisted across reloads using server started_at + local anchor.
  const [nowTs, setNowTs] = useState(Date.now());
  const [startAnchorIso, setStartAnchorIso] = useState<string | null>(null);
  const [finalElapsed, setFinalElapsed] = useState<number | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    timerRef.current = setInterval(() => setNowTs(Date.now()), 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  const { status, progress, isComplete, callLog, inFlightCalls } = useVerificationStatus(jobId ?? null);
  const startKey = jobId ? `jobStart:${jobId}` : null;

  // Seed from local storage so refresh doesn't flash to zero before status arrives.
  useEffect(() => {
    if (!startKey) return;
    const stored = localStorage.getItem(startKey);
    if (stored) setStartAnchorIso(stored);
  }, [startKey]);

  // Server started_at is canonical source of truth.
  useEffect(() => {
    if (!startKey || !status?.started_at) return;
    setStartAnchorIso(status.started_at);
    localStorage.setItem(startKey, status.started_at);
  }, [status?.started_at, startKey]);

  // Freeze timer the moment backend reports any terminal state.
  useEffect(() => {
    if (!status || finalElapsed !== null) return;
    const isTerminal =
      status.status === 'completed' ||
      status.status === 'failed' ||
      status.status === 'cancelled';
    if (isTerminal && status.started_at && status.completed_at) {
      if (timerRef.current) clearInterval(timerRef.current);
      const startedMs = parseServerTimestampMs(status.started_at);
      const completedMs = parseServerTimestampMs(status.completed_at);
      if (startedMs !== null && completedMs !== null) {
        const secs = Math.max(0, Math.floor((completedMs - startedMs) / 1000));
        setFinalElapsed(secs);
      }
    }
  }, [status, finalElapsed]);

  useEffect(() => {
    if (!status) return;
    if (status.status === 'completed' || status.status === 'failed' || status.status === 'cancelled') {
      setDone(true);
    }
  }, [status]);

  // Clear running anchor for terminal states.
  useEffect(() => {
    if (!startKey || !status) return;
    if (status.status === 'completed' || status.status === 'failed' || status.status === 'cancelled') {
      localStorage.removeItem(startKey);
    }
  }, [status, startKey]);

  // Detect analysis activity for the notification dot
  useEffect(() => {
    if (!analysisActive && status) {
      const cats = status.category_counts || {};
      const hasAnalysable =
        (cats['UNKNOWN'] || 0) + (cats['VOICEMAIL'] || 0) + (cats['SWITCHBOARD'] || 0);
      if (hasAnalysable > 0) setAnalysisActive(true);
    }
  }, [status, analysisActive]);

  if (!jobId) {
    navigate('/upload');
    return null;
  }

  const activeStartIso = status?.started_at || startAnchorIso;
  const activeStartMs = parseServerTimestampMs(activeStartIso);
  const computedElapsed = activeStartMs !== null
    ? Math.max(0, Math.floor((nowTs - activeStartMs) / 1000))
    : null;
  const displayElapsed = finalElapsed ?? computedElapsed;
  const mm = displayElapsed !== null ? String(Math.floor(displayElapsed / 60)).padStart(2, '0') : '--';
  const ss = displayElapsed !== null ? String(displayElapsed % 60).padStart(2, '0') : '--';
  const isFullyDone = status?.status === 'completed';
  const canCancel =
    !status ||
    status.status === 'pending' ||
    (status.status === 'processing' && status.processed_count < status.total_numbers);

  const handleCancel = async () => {
    setCancelling(true);
    setCancelError(null);
    try {
      await verifyApi.cancelJob(jobId);
      // Stop timer on cancel too
      if (timerRef.current) clearInterval(timerRef.current);
      const startIso = status?.started_at || startAnchorIso;
      const startMs = parseServerTimestampMs(startIso);
      const secs = startMs !== null ? Math.max(0, Math.floor((Date.now() - startMs) / 1000)) : 0;
      setFinalElapsed(secs);
      setDone(true);
    } catch {
      setCancelError('Failed to cancel � the job may have already completed.');
    } finally {
      setCancelling(false);
    }
  };

  const handleNewVerification = () => {
    resetState();
    navigate('/upload');
  };

  return (
    <div style={{ padding: '0 0 40px' }}>
      {/* Page header */}
      <div style={{ marginBottom: 24, paddingTop: 8, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, color: 'var(--text-strong)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            {done
              ? isFullyDone ? 'Run Complete' : 'Run Ended'
              : 'Dialing In Progress'}
          </h2>
          <p style={{ margin: '4px 0 0', color: 'var(--text-soft)', fontSize: 14 }}>Step 4 of 4</p>
        </div>

        {/* Unified timer � always visible, stops only when fully complete */}
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4,
        }}>
          <div style={{
            fontFamily: 'monospace', fontSize: 22, fontWeight: 700, letterSpacing: 2,
            color: isFullyDone ? 'var(--status-success-fg)' : 'var(--amber)',
            background: isFullyDone ? 'var(--status-success-bg)' : 'var(--status-analysis-processing-bg)',
            padding: '6px 16px', borderRadius: 10,
            border: `2px solid ${isFullyDone ? 'var(--status-success-fg)' : 'var(--teal)'}`,
            minWidth: 100, textAlign: 'center',
          }}>
            {'\u23F1'} {mm}:{ss}
          </div>
          {!done && inFlightCalls.size > 0 && (
            <span style={{
              fontSize: 11, color: 'var(--amber)', fontFamily: 'var(--font-data)',
              letterSpacing: '0.04em', display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap',
            }}>
              <span style={{
                width: 7, height: 7, borderRadius: '50%', background: 'var(--amber)',
                display: 'inline-block', animation: 'pulse-dot 1.1s ease-in-out infinite',
              }} />
              {inFlightCalls.size} {inFlightCalls.size === 1 ? 'call' : 'calls'} ringing now
            </span>
          )}
          <span style={{ fontSize: 11, color: 'var(--text-faint)' }}>
            {displayElapsed !== null
              ? (isFullyDone
                  ? 'Total time (dial + AI)'
                  : done
                  ? 'AI analysis running�'
                  : 'Elapsed time')
              : (activeStartIso
                  ? `Started: ${new Date(activeStartIso).toLocaleTimeString()}`
                  : 'Start time unavailable')}
          </span>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div style={{ display: 'flex', borderBottom: '2px solid var(--border-subtle)', marginBottom: 20, gap: 4 }}>
        <TabButton
          active={activeTab === 'dialer'}
          onClick={() => setActiveTab('dialer')}
          icon="📞"
          label="Dialer Progress"
        />
        <TabButton
          active={activeTab === 'analysis'}
          onClick={() => setActiveTab('analysis')}
          icon="🤖"
          label="AI Analysis"
          dot={analysisActive && !isFullyDone}
        />
      </div>

      {cancelError && (
        <div style={{
          background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)', padding: '12px 16px',
          borderRadius: 8, marginBottom: 16, fontSize: 14,
          border: '1px solid var(--status-danger-fg)',
        }}>
          ❌ {cancelError}
        </div>
      )}

      {/* "AI still running" banner � shows ONLY when dialing is done but job not yet completed */}
      {done && !isFullyDone && status?.status !== 'failed' && status?.status !== 'cancelled' && (
        <div style={{
          background: 'var(--status-analysis-processing-bg)', border: '1px solid var(--status-analysis-processing-fg)',
          borderRadius: 8, padding: '10px 16px',
          marginBottom: 16, fontSize: 13, color: 'var(--status-analysis-processing-fg)',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <span style={{
            width: 8, height: 8, borderRadius: '50%', background: 'var(--status-analysis-processing-fg)',
            display: 'inline-block', animation: 'pulse-dot 1.2s ease-in-out infinite',
          }} />
          Dialing complete — AI analysis still running. Timer will stop when everything finishes.
          <button
            onClick={() => setActiveTab('analysis')}
            style={{
              marginLeft: 'auto', fontSize: 12,
              color: 'var(--status-analysis-processing-fg)',
              background: 'none', border: 'none', cursor: 'pointer',
              textDecoration: 'underline', padding: 0,
            }}
          >
            View AI tab
          </button>
        </div>
      )}

      {/* Tab Content */}
      <div style={{ marginBottom: 24 }}>
        {activeTab === 'dialer' ? (
          <div style={{
            border: '1px solid var(--border)', borderRadius: 2,
            background: 'var(--surface)', overflow: 'hidden',
            boxShadow: '5px 5px 0 rgba(0,0,0,0.45)',
          }}>
            {done && status ? (
              <ResultsView jobId={jobId} status={status} />
            ) : (
              <ProgressTracker
                status={status}
                progress={progress}
                isComplete={isComplete}
                callLog={callLog}
                inFlightCalls={inFlightCalls}
              />
            )}
          </div>
        ) : activeTab === 'analysis' ? (
          <AnalysisTab jobId={jobId} isActive={activeTab === 'analysis'} />
        ) : null}
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
        {isFullyDone ? (
          <>
            <button
              onClick={handleNewVerification}
              style={{
                background: 'linear-gradient(135deg, #8a2000, #5c0000)',
                color: 'var(--text)', border: '2px solid var(--teal)',
                padding: '11px 28px', borderRadius: 8,
                cursor: 'pointer', fontSize: 14, fontWeight: 600,
                boxShadow: '5px 5px 0 rgba(0,0,0,0.45)',
              }}
            >
               New Run
            </button>
            <Link
              to="/history"
              style={{
                background: 'var(--surface-subtle)', color: 'var(--text)', border: '1px solid var(--border)',
                padding: '11px 28px', borderRadius: 8, cursor: 'pointer', fontSize: 14,
                textDecoration: 'none', display: 'inline-flex', alignItems: 'center',
                fontWeight: 500,
              }}
            >
               View Runs
            </Link>
          </>
        ) : (
          canCancel ? (
            <button
              onClick={handleCancel}
              disabled={cancelling}
              style={{
                background: cancelling ? 'var(--status-neutral-bg)' : 'linear-gradient(135deg, #8a2000, #5c0000)',
                color: cancelling ? 'var(--text-faint)' : 'var(--text)', border: '2px solid var(--teal)',
                padding: '11px 28px', borderRadius: 8,
                cursor: cancelling ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600,
              }}
            >
              {cancelling ? 'Cancelling…' : ' Cancel Run'}
            </button>
          ) : (
            <div style={{ fontSize: 13, color: 'var(--text-soft)' }}>
              Finalizing AI analysis/export…
            </div>
          )
        )}
      </div>

      <style>{`
        @keyframes pulse-dot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(0.85); }
        }
      `}</style>
    </div>
  );
};

interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  icon: string;
  label: string;
  dot?: boolean;
}

const TabButton: React.FC<TabButtonProps> = ({ active, onClick, icon, label, dot }) => (
  <button
    onClick={onClick}
    style={{
      position: 'relative',
      padding: '10px 18px', border: 'none', background: 'transparent',
      cursor: 'pointer', fontSize: 14,
      fontWeight: active ? 700 : 500,
      color: active ? 'var(--amber)' : 'var(--text-soft)',
      borderBottom: active ? '2px solid var(--amber)' : '2px solid transparent',
      marginBottom: -2,
      transition: 'color 0.15s, border-color 0.15s',
      display: 'inline-flex', alignItems: 'center', gap: 6,
    }}
  >
    {icon} {label}
    {dot && (
      <span style={{
        width: 7, height: 7, borderRadius: '50%',
        background: 'var(--amber)', display: 'inline-block', marginLeft: 2,
        animation: 'pulse-dot 1.5s ease-in-out infinite',
      }} />
    )}
  </button>
);

export default Processing;
